import type { CarrierRequestListItem } from '#/shared/api/generated/shipment/models'

import { CARRIER_REQUEST_STATUS } from './constants'
import type { CarrierRequestItem } from './types'

function toFiniteNumber(
  raw: number | string | null | undefined,
): number | null {
  if (raw == null || raw === '') return null
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) ? num : null
}

function deriveRespondedAt(item: CarrierRequestListItem): string | undefined {
  return (
    item.accepted_at_by_carrier ??
    item.canceled_at ??
    item.declined_at ??
    item.declined_at_by_carrier ??
    item.accepted_at ??
    undefined
  )
}

interface MapOptions {
  /** COD / due on delivery — the baseline the requested price is compared against. */
  dueOnDelivery?: number
  carrierPay?: number
  distanceMiles?: number
}

function buildPrice(
  requestedPrice: number,
  options: MapOptions,
): CarrierRequestItem['price'] {
  const miles = options.distanceMiles
  const cod = options.dueOnDelivery
  return {
    amount: requestedPrice,
    pricePerMile: miles && miles > 0 ? requestedPrice / miles : undefined,
    deltaFromCod: cod != null && cod > 0 ? requestedPrice - cod : undefined,
  }
}

function priceMatchesCarrierPay(
  requestedPrice: number | null,
  carrierPay: number | undefined,
): boolean {
  if (requestedPrice == null || carrierPay == null) return false
  return Math.abs(requestedPrice - carrierPay) < 0.005
}

export function mapCarrierRequestListItem(
  item: CarrierRequestListItem,
  options: MapOptions = {},
): CarrierRequestItem {
  const requestedPrice = toFiniteNumber(item.requested_price)
  const counterPrice = toFiniteNumber(item.counter_offer_price)
  return {
    id: item.id,
    carrier: {
      name: item.carrier_name,
      contactName: item.carrier_contact_name ?? undefined,
      phone: item.carrier_phone ?? undefined,
      isVerified: item.is_verified,
      acceptsSuperPay: item.is_super,
    },
    dates: {
      pickupAt: item.pickup_date ?? undefined,
      deliveryAt: item.delivery_date ?? undefined,
    },
    price:
      requestedPrice != null ? buildPrice(requestedPrice, options) : undefined,
    status: item.status,
    requestedAt: item.created_at,
    respondedAt: deriveRespondedAt(item),
    counterOfferSentAt: item.counter_offer_sent_at ?? undefined,
    brokerCounterPrice: counterPrice ?? undefined,
    matchesCarrierPay: priceMatchesCarrierPay(
      requestedPrice,
      options.carrierPay,
    ),
  }
}

export function mapCarrierRequestList(
  items: CarrierRequestListItem[],
  options: MapOptions = {},
): CarrierRequestItem[] {
  return items.map((raw) => mapCarrierRequestListItem(raw, options))
}

export function countAcceptedRequests(items: CarrierRequestListItem[]): number {
  return items.filter((item) => item.status === CARRIER_REQUEST_STATUS.accepted)
    .length
}
